import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { masterApi } from "@/contexts/MasterAuthContext";
import { ArrowLeft, Save, Plus, Minus } from "lucide-react";

export default function MasterClubDetail() {
    const { clubId } = useParams();
    const navigate = useNavigate();
    const [club, setClub] = useState(null);
    const [plans, setPlans] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState("");
    const [creditAmount, setCreditAmount] = useState(0);

    const load = () => {
        masterApi.get(`/master/clubs/${clubId}`).then((r) => setClub(r.data));
    };
    useEffect(() => {
        load();
        masterApi.get("/master/plans").then((r) => setPlans(r.data));
        masterApi.get("/master/templates").then((r) => setTemplates(r.data));
    }, [clubId]);

    if (!club) return <div data-testid="club-detail-loading">Yükleniyor…</div>;

    const set = (k, v) => setClub((c) => ({ ...c, [k]: v }));

    const save = async () => {
        setSaving(true); setMessage("");
        try {
            const payload = {
                name: club.name, short_name: club.short_name, description: club.description,
                domains: club.domains, logo_url: club.logo_url, favicon_url: club.favicon_url,
                primary_color: club.primary_color, secondary_color: club.secondary_color, accent_color: club.accent_color, bg_color: club.bg_color,
                theme_template: club.theme_template, font_family: club.font_family,
                instagram_handle: club.instagram_handle, website_url: club.website_url,
                mackolik_team_id: club.mackolik_team_id, mackolik_team_name: club.mackolik_team_name,
                subscription_plan: club.subscription_plan, is_active: club.is_active,
            };
            await masterApi.put(`/master/clubs/${clubId}`, payload);
            setMessage("Kaydedildi");
            setTimeout(() => setMessage(""), 2500);
            load();
        } catch (e) {
            setMessage(e?.response?.data?.detail || "Hata");
        } finally { setSaving(false); }
    };

    const adjustCredit = async (amount) => {
        if (!amount) return;
        try {
            await masterApi.post(`/master/clubs/${clubId}/credit-adjust`, { amount, note: "Master panel ayarlaması" });
            setCreditAmount(0);
            load();
        } catch (e) {
            alert(e?.response?.data?.detail || "Hata");
        }
    };

    const sub = club.subscription || {};
    return (
        <div data-testid={`master-club-detail-${clubId}`}>
            <div className="flex items-center justify-between mb-8">
                <div>
                    <button data-testid="back-btn" onClick={() => navigate("/master/clubs")} className="inline-flex items-center gap-2 text-sm text-neutral-400 hover:text-yellow-400 mb-3">
                        <ArrowLeft size={14}/> Kulüplere Dön
                    </button>
                    <h1 className="text-4xl font-extrabold tracking-tight">{club.name}</h1>
                    <p className="text-neutral-500 text-sm mt-1">ID: <code className="text-yellow-400">{club.id}</code></p>
                </div>
                <div className="flex items-center gap-3">
                    {message && <span className="text-emerald-400 text-sm">{message}</span>}
                    <button data-testid="save-btn" onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-5 py-3 bg-yellow-500 hover:bg-yellow-400 disabled:opacity-50 text-black font-bold uppercase tracking-wider text-sm rounded-lg">
                        <Save size={16}/> {saving ? "Kaydediliyor…" : "Kaydet"}
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                    <Section title="Temel Bilgiler">
                        <FormRow label="Kulüp Adı"><Input value={club.name} onChange={(v) => set("name", v)} testId="club-name" /></FormRow>
                        <FormRow label="Kısa İsim"><Input value={club.short_name} onChange={(v) => set("short_name", v)} testId="club-short-name" /></FormRow>
                        <FormRow label="Açıklama"><textarea data-testid="club-description" value={club.description || ""} onChange={(e) => set("description", e.target.value)} rows={2} className="liv-master-input" /></FormRow>
                        <FormRow label="Domain(ler)">
                            <Input value={(club.domains || []).join(", ")} onChange={(v) => set("domains", v.split(",").map(s => s.trim()).filter(Boolean))} testId="club-domains" />
                        </FormRow>
                        <FormRow label="Aktif">
                            <select data-testid="club-active" value={club.is_active ? "1" : "0"} onChange={(e) => set("is_active", e.target.value === "1")} className="liv-master-input">
                                <option value="1">Aktif</option>
                                <option value="0">Pasif</option>
                            </select>
                        </FormRow>
                    </Section>

                    <Section title="Görsel Kimlik">
                        <FormRow label="Logo URL"><Input value={club.logo_url} onChange={(v) => set("logo_url", v)} testId="club-logo" /></FormRow>
                        <FormRow label="Favicon URL"><Input value={club.favicon_url} onChange={(v) => set("favicon_url", v)} testId="club-favicon" /></FormRow>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            {[["primary_color","Primary"],["secondary_color","Secondary"],["accent_color","Accent"],["bg_color","BG"]].map(([k, l]) => (
                                <FormRow key={k} label={l}>
                                    <div className="flex gap-2">
                                        <input data-testid={`color-${k}`} type="color" value={club[k] || "#000"} onChange={(e) => set(k, e.target.value)} className="w-10 h-9 rounded cursor-pointer" />
                                        <input value={club[k] || ""} onChange={(e) => set(k, e.target.value)} className="liv-master-input font-mono text-xs flex-1" />
                                    </div>
                                </FormRow>
                            ))}
                        </div>
                        <FormRow label="Homepage Şablonu">
                            <select data-testid="club-template" value={club.theme_template || "classic"} onChange={(e) => set("theme_template", e.target.value)} className="liv-master-input">
                                {templates.map((t) => <option key={t.key} value={t.key}>{t.name} — {t.description}</option>)}
                            </select>
                        </FormRow>
                        <FormRow label="Instagram"><Input value={club.instagram_handle} onChange={(v) => set("instagram_handle", v)} testId="club-instagram" /></FormRow>
                        <FormRow label="Website URL"><Input value={club.website_url} onChange={(v) => set("website_url", v)} testId="club-website" /></FormRow>
                    </Section>

                    <Section title="Lig Verisi (Sporzip)">
                        <FormRow label="Takım ID"><Input value={club.mackolik_team_id} onChange={(v) => set("mackolik_team_id", v)} testId="club-macko-id" /></FormRow>
                        <FormRow label="Görünen İsim"><Input value={club.mackolik_team_name} onChange={(v) => set("mackolik_team_name", v)} testId="club-macko-name" /></FormRow>
                    </Section>
                </div>

                <div className="space-y-6">
                    <Section title="Abonelik">
                        <FormRow label="Paket">
                            <select data-testid="club-plan" value={club.subscription_plan || "starter"} onChange={(e) => set("subscription_plan", e.target.value)} className="liv-master-input">
                                {plans.map((p) => <option key={p.key} value={p.key}>{p.display_name} — ₺{p.monthly_price_try.toLocaleString("tr-TR")}/ay</option>)}
                            </select>
                        </FormRow>
                        <div className="bg-[#0a0a0a] border border-[#1f1f1f] rounded-lg p-4 mt-3">
                            <div className="text-xs text-neutral-500 uppercase tracking-widest mb-2">Mevcut Kredi</div>
                            <div className="text-3xl font-extrabold text-yellow-400">{sub.credit_balance || 0} <span className="text-sm text-neutral-500">/ {sub.monthly_credit_limit || 0}</span></div>
                            <div className="text-xs text-neutral-500 mt-2">All-time kullanılan: {sub.total_used_all_time || 0}</div>

                            <div className="flex gap-2 mt-4">
                                <input data-testid="credit-amount" type="number" value={creditAmount} onChange={(e) => setCreditAmount(parseInt(e.target.value) || 0)} className="liv-master-input flex-1" placeholder="Miktar" />
                                <button data-testid="credit-add" onClick={() => adjustCredit(Math.abs(creditAmount))} className="px-3 py-2 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 rounded-lg">
                                    <Plus size={14}/>
                                </button>
                                <button data-testid="credit-remove" onClick={() => adjustCredit(-Math.abs(creditAmount))} className="px-3 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg">
                                    <Minus size={14}/>
                                </button>
                            </div>
                        </div>
                    </Section>

                    <Section title="Kullanıcılar">
                        {(club.users || []).map((u) => (
                            <div key={u.id} className="text-sm py-2 border-b border-[#1f1f1f] last:border-0">
                                <div className="font-semibold">{u.name}</div>
                                <div className="text-xs text-neutral-500">{u.email} · {u.role}</div>
                            </div>
                        ))}
                    </Section>
                </div>
            </div>

            <style>{`
                .liv-master-input { width: 100%; background: #0a0a0a; border: 1px solid #2a2a2a; padding: 0.5rem 0.75rem; border-radius: 0.5rem; color: #fff; outline: none; font-size: 0.875rem; transition: border-color .15s; }
                .liv-master-input:focus { border-color: #f5dc4c; }
            `}</style>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <div className="bg-[#121212] border border-[#2a2a2a] rounded-2xl p-5">
            <h3 className="font-bold text-base mb-4">{title}</h3>
            <div className="space-y-3">{children}</div>
        </div>
    );
}
function FormRow({ label, children }) {
    return (
        <div>
            <label className="block text-xs uppercase tracking-widest text-neutral-500 mb-1.5">{label}</label>
            {children}
        </div>
    );
}
function Input({ value, onChange, testId }) {
    return <input data-testid={testId} value={value || ""} onChange={(e) => onChange(e.target.value)} className="liv-master-input" />;
}
